'use client';

import { useEffect, useRef, useState, useCallback } from 'react';

const RECOVERABLE_STATUS = 'training';
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export interface KnowledgeTrainingStatus {
  status: 'needs_training' | 'training' | 'completed' | 'failed';
  message: string;
  errorMessage?: string | null;
  lastTrainedAt?: string | null;
  summary?: string | null;
  topics?: string[];
  exampleQuestions?: string[];
  documentCount?: number;
}

export interface UseKnowledgeTrainingStatusOptions {
  sourceId: string | null;
  enabled?: boolean;
  pollIntervalMs?: number;
}

export function useKnowledgeTrainingStatus({
  sourceId,
  enabled = true,
  pollIntervalMs = 3000,
}: UseKnowledgeTrainingStatusOptions) {
  const [status, setStatus] = useState<KnowledgeTrainingStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const recoveryTriggeredRef = useRef(false);
  const statusFirstSeenRef = useRef<{ status: string; at: number } | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!sourceId) return null;

    try {
      const res = await fetch(`/api/knowledge-sources/${sourceId}/train`);
      if (!res.ok) return null;

      const data = await res.json();
      const statusData: KnowledgeTrainingStatus = {
        status: data.status || 'needs_training',
        message: data.message || '',
        errorMessage: data.errorMessage ?? null,
        lastTrainedAt: data.lastTrainedAt ?? null,
        summary: data.summary ?? null,
        topics: data.topics ?? [],
        exampleQuestions: data.exampleQuestions ?? [],
        documentCount: data.documentCount,
      };
      setStatus(statusData);
      return data;
    } catch {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [sourceId]);

  const triggerRetrain = useCallback(() => {
    if (!sourceId) return;
    fetch(`/api/knowledge-sources/${sourceId}/train`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {});
  }, [sourceId]);

  useEffect(() => {
    if (!sourceId || !enabled) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const poll = () => {
      fetchStatus().then((data) => {
        if (!data) return;
        if (data.status === 'completed' || data.status === 'failed') {
          if (intervalId) clearInterval(intervalId);
          return;
        }
        if (
          data.status === RECOVERABLE_STATUS &&
          !recoveryTriggeredRef.current
        ) {
          const now = Date.now();
          const prev = statusFirstSeenRef.current;
          if (prev?.status === data.status) {
            if (now - prev.at > STALE_THRESHOLD_MS) {
              recoveryTriggeredRef.current = true;
              statusFirstSeenRef.current = null;
              triggerRetrain();
            }
          } else {
            statusFirstSeenRef.current = { status: data.status, at: now };
          }
        }
      });
    };

    poll();
    intervalId = setInterval(poll, pollIntervalMs);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [sourceId, enabled, pollIntervalMs, fetchStatus, triggerRetrain]);

  const retry = useCallback(async () => {
    if (!sourceId) return;

    recoveryTriggeredRef.current = false;
    statusFirstSeenRef.current = null;
    await fetch(`/api/knowledge-sources/${sourceId}/train`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });

    setStatus((prev) =>
      prev
        ? {
            ...prev,
            status: 'training',
            message: 'Training in progress...',
            errorMessage: null,
          }
        : null
    );

    fetchStatus();
  }, [sourceId, fetchStatus]);

  return {
    status,
    isLoading,
    isComplete: status?.status === 'completed',
    isError: status?.status === 'failed',
    isTraining: status?.status === 'training',
    retry,
    refetch: fetchStatus,
  };
}
